import {
    CanvasPage,
    RoomStoreState,
    OBSState,
    RoomStageType,
    CanvasThumbs,
    Stream,
    StreamPushStatus, SDKFloatButton, DeviceSetting, SDKTab, Trailer
} from "./room-defines";
import {DrawObject} from "../draw-board";
import {RoomBasic, RoomCounter, RoomMode, RoomState, RoomUser, RoomVirtualUser, ScreenMode} from "../room-engine/types";
import Logger from "../logger";
import {reactive, watch} from "vue";
import $f from "../feature-config";

export class RoomStore {
    public state: RoomStoreState;

    constructor() {
        this.state = reactive<RoomStoreState>({
            isLive: true,
            sideOpened: true,
            isReplay: false,
            id: 0,
            type: "",
            name: "",
            status: RoomState.UNSTART,
            mode: RoomMode.NORMAL,
            pseudoVideoUrl: "",
            logo: "",
            fileDomain: "",
            allMute: "none",
            globalChatMute: false,
            forceChatOnly: false,
            showComboStats: true,
            startAt: 0,
            endAt: 0,
            actualStartAt: 0,
            actualEndAt: 0,
            hasOvertime: false,
            talkerLimit: 0,
            isTalking: false,
            recordEnable: false,
            recordStatus: "unstarted",
            replayEnable: false,
            remainTime: 0,
            user: {
                id: 0,
                name: "",
                role: "",
                mute: false,
                groupNo: "",
                groupName: "",
                pmOnline: false,
            },
            counter: {
                online: 0,
                maxOnline: 0,
                enterNum: 0,
                likeNum: 0,
            },
            virtualUser: {
                virtualUserBase: 0,
                virtualUserMultiple: 1,
                virtualUserMode: "",
            },
            contents: [],
            uploadModalVisible: false,
            fullscreen: false,
            dragEnable: false,
            stream: {
                id: 0,
                pushStatus: StreamPushStatus.NONE,
                rtcRtt: "0",
                rtcPacketsLostRate: "0",
                rtcBitrate: "0",
                rtcNetworkQualityLevel: 0,
                talkStreamToken: "",
            },
            playerState: "none",
            canvasData: [],
            hotKeyEnable: true,
            docThumbnails: [],
            blankThumbnails: [],
            cameraDeviceId: "",
            micDeviceId: "",
            micVolume: 100,
            volumeMeter: 0,
            spkDeviceId: "",
            deviceReady: false,
            danmakuEnable: false,
            chatFiltered: false,
            cameraVisible: true,
            leadQrcode: false,
            leadQrcodeUrl: "",
            sharePosterUrl: "",
            finishCallbackUrl: "",
            obs: {streamId: 0, pushServer: "", pushKey: "", flvPlayUrl: "", hlsPlayUrl: ""},
            screenMode: ScreenMode.PORTRAIT,
            speakerPage: 0,
            showHelpPopup: false,
            nativeBridgeConnected: false,
            nativeKeyboardHeight: 0,
            videoElementVisible: false,
            sdkFloatButtons: [],
            sdkTabs: [],
            trailer: {} as Trailer,
            sdkBuyGoodsNo: "",
            historyChats: [],
        });

        watch(() => this.state.isLive, (isLive: boolean) => {
            this.state.isReplay = !isLive;
        })
    }

    setIsLive(isLive: boolean): void {
        this.state.isLive = isLive;
    }

    setIsTalking(isTalking: boolean): void {
        this.state.isTalking = isTalking;
    }

    setStatus(status: RoomState): void {
        Logger.debug("room status to " + status);
        this.state.status = status;
    }

    setUser(user: Partial<RoomUser>): void {
        Logger.debug("room user", user);
        Object.assign(this.state.user, user);
    }

    setRoomBasic(room: Partial<RoomBasic>): void {
        Object.assign(this.state, room);
    }

    setCounter(counter: Partial<RoomCounter>): void {
        Object.assign(this.state.counter, counter);
    }

    setVirtualUser(virtualUser: Partial<RoomVirtualUser>): void {
        Object.assign(this.state.virtualUser, virtualUser);
    }

    getVirtualUserNum(): number {
        const online = this.state.counter.online;
        if (!$f.virtualUser) {
            return online;
        }
        const virtual = this.state.virtualUser;
        return online * virtual.virtualUserMultiple + virtual.virtualUserBase;
    }

    setDevice(setting: Partial<DeviceSetting>): void {
        if (typeof (setting.micVolume) !== 'undefined') {
            this.state.micVolume = setting.micVolume;
        }

        if (setting.cameraDeviceId && setting.cameraDeviceId.length > 0) {
            this.state.cameraDeviceId = setting.cameraDeviceId;
        }

        if (setting.micDeviceId && setting.micDeviceId.length > 0) {
            this.state.micDeviceId = setting.micDeviceId;
        }

        if (this.state.micDeviceId) {
            this.state.deviceReady = true;
        }
    }

    setFullscreen(fullscreen: boolean): void {
        this.state.fullscreen = fullscreen;
    }

    setSideOpened(opened: boolean): void {
        this.state.sideOpened = opened;
    }

    setDanmakuEnable(enable: boolean): void {
        this.state.danmakuEnable = enable;
    }

    setChatFiltered(filtered: boolean): void {
        this.state.chatFiltered = filtered;
    }

    setCameraVisible(visible: boolean): void {
        this.state.cameraVisible = visible;
    }

    setShowHelpPopup(show: boolean): void {
        this.state.showHelpPopup = show;
    }

    // eslint-disable-next-line
    addObject(canvasData: Array<CanvasPage>, contentId: number, page: number, lastDrawAt: number, object: DrawObject): any {
        if (!canvasData || canvasData.length === 0) {
            const canvasPage: CanvasPage = {
                lastDrawAt: lastDrawAt,
                contentId: contentId,
                page: page,
                objects: [object]
            };
            canvasData = [];
            canvasData.push(canvasPage);
            return canvasData;
        }

        let resultCanvasPage = undefined;
        for (const data of canvasData) {
            if (data.contentId === contentId && data.page === page) {
                resultCanvasPage = data;
                break;
            }
        }

        if (!resultCanvasPage) {
            resultCanvasPage = {
                lastDrawAt: lastDrawAt,
                contentId: contentId,
                page: page,
                objects: []
            };
            canvasData.push(resultCanvasPage);
        }

        resultCanvasPage.lastDrawAt = lastDrawAt;
        resultCanvasPage.objects.push(object);

        return canvasData;
    }

    addCanvasObjects(contentId: number, page: number, lastDrawAt: number, objects: Array<DrawObject>): void {
        for (const object of objects) {
            this.state.canvasData = this.addObject(this.state.canvasData, contentId, page, lastDrawAt, object);
        }
    }

    clearCanvasObjects(contentId: number, page: number): void {
        if (!this.state.canvasData || this.state.canvasData.length === 0) {
            return;
        }

        for (let data of this.state.canvasData) {
            if (data.contentId === contentId && data.page === page) {
                data = {
                    contentId: 0,
                    page: 0,
                    lastDrawAt: 0,
                    objects: [],
                };
                break;
            }
        }
    }

    getCanvasObject(contentId: number, page: number): Array<DrawObject> {
        if (!this.state.canvasData || this.state.canvasData.length === 0) {
            return [];
        }

        let objects: DrawObject[] = [];
        for (const data of this.state.canvasData) {
            if (data.contentId === contentId && data.page === page) {
                objects = data.objects;
                break;
            }
        }

        if (!objects) {
            objects = [];
        }
        return objects;
    }

    getCanvasLastDrawAt(contentId: number, page: number): number {
        if (!this.state.canvasData || this.state.canvasData.length === 0) {
            return 0;
        }

        let lastDrawAt = 0;
        for (const data of this.state.canvasData) {
            if (data.contentId === contentId && data.page === page) {
                lastDrawAt = data.lastDrawAt;
                break;
            }
        }
        return lastDrawAt;
    }

    setSpeakerPage(page: number): void {
        this.state.speakerPage = page;
    }


    // eslint-disable-next-line
    setLocalThumbnail(thumbs: any, type: RoomStageType, page: number) {
        const canvasThumbs: string[] = [];
        for (let i = 1; i <= page; i++) {
            if (!thumbs || !thumbs.pages) {
                canvasThumbs.push("");
                continue;
            }

            canvasThumbs.push(this.findThumbnail(i, thumbs));
        }
        if (type === RoomStageType.DOCUMENT) {
            this.state.docThumbnails = canvasThumbs;
        } else {
            this.state.blankThumbnails = canvasThumbs;
        }
    }

    findThumbnail(i: number, canvasThumbs: CanvasThumbs): string {
        for (const page of canvasThumbs.pages) {
            if (page === i) {
                return canvasThumbs.prefix + page + ".png";
            }
        }
        return "";
    }

    setStream(stream: Partial<Stream>): void {
        Object.assign(this.state.stream, stream);
    }

    setUploadModalVisible(visible: boolean): void {
        this.state.uploadModalVisible = visible;
    }

    setObs(obs: OBSState): void {
        this.state.obs = obs;
    }

    setNativeBridgeConnected(connected: boolean): void {
        this.state.nativeBridgeConnected = connected;
    }

    setNativeKeyboardHeight(height: number): void {
        this.state.nativeKeyboardHeight = height;
    }

    setVideoElementVisible(visible: boolean): void {
        this.state.videoElementVisible = visible;
    }

    addSdkFloatButton(button: SDKFloatButton): void {
        this.state.sdkFloatButtons.push(button);
    }

    setTabs(tabs: SDKTab[]): void {
        this.state.sdkTabs = tabs;
    }

    setTrailer(trailer: Trailer): void {
        this.state.trailer = trailer;
    }

    buyGoods(no: string): void {
        this.state.sdkBuyGoodsNo = no;
    }
}

export default new RoomStore();
